import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useCollection } from "../../../hooks/useCollection";
import { useFirestore } from "../../../hooks/useFirestore";
import { timestamp } from "../../../firebase/config";
import { useAuthContext } from "../../../hooks/useAuthContext";
import "./AvvisiDipendenti.css";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export default function AvvisiDipendenti() {
  const { documents: avvisi, error } = useCollection("avvisi", null, [
    "createdAt",
    "desc",
  ]);
  const { documents: users } = useCollection("users");
  const { addDocument, deleteDocument, updateDocument } =
    useFirestore("avvisi");
  const { user } = useAuthContext();

  const [titolo, setTitolo] = useState("");
  const [contenuto, setContenuto] = useState("");
  const [priorita, setPriorita] = useState("media"); // bassa, media, alta
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (users) {
      const options = users.map((u) => {
        // Recupera nome e cognome dall'anagrafica se disponibili, altrimenti usa displayName o email
        const nome = u.anagrafica?.nome || "";
        const cognome = u.anagrafica?.cognome || "";
        let fullName = "";

        if (nome || cognome) {
          fullName = `${nome} ${cognome}`.trim();
        } else {
          fullName = u.displayName || u.email || "Utente senza nome";
        }

        // Recupera il settore dall'anagrafica
        const settore = u.anagrafica?.settore || "Nessun settore";

        return {
          value: u.id,
          label: `${fullName} - ${settore}`,
        };
      });
      setUserOptions(options);
    }
  }, [users]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const destinatari = selectedUsers.map((u) => u.value);

    if (editingId) {
      await updateDocument(editingId, {
        titolo,
        contenuto,
        priorita,
        destinatari,
        updatedAt: timestamp.fromDate(new Date()),
      });
      setEditingId(null);
    } else {
      await addDocument({
        titolo,
        contenuto,
        priorita,
        destinatari,
        createdAt: timestamp.fromDate(new Date()),
        autore: user.displayName || user.email,
        visibile: true,
      });
    }

    resetForm();
  };

  const resetForm = () => {
    setTitolo("");
    setContenuto("");
    setPriorita("media");
    setSelectedUsers([]);
    setEditingId(null);
  };

  const handleEdit = (avviso) => {
    setTitolo(avviso.titolo);
    setContenuto(avviso.contenuto);
    setPriorita(avviso.priorita);

    // Ripristina gli utenti selezionati
    if (
      avviso.destinatari &&
      avviso.destinatari.length > 0 &&
      userOptions.length > 0
    ) {
      const selected = userOptions.filter((opt) =>
        avviso.destinatari.includes(opt.value),
      );
      setSelectedUsers(selected);
    } else {
      setSelectedUsers([]);
    }

    setEditingId(avviso.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questo avviso?")) {
      await deleteDocument(id);
    }
  };

  const toggleVisibility = async (avviso) => {
    await updateDocument(avviso.id, {
      visibile: !avviso.visibile,
    });
  };

  const getDestinatariNames = (destinatariIds) => {
    if (!destinatariIds || destinatariIds.length === 0) return "Tutti";

    // Se ci sono troppi destinatari (es. > 5), mostra il numero
    if (destinatariIds.length > 5)
      return `${destinatariIds.length} destinatari`;

    return destinatariIds
      .map((id) => {
        const user = userOptions.find((opt) => opt.value === id);
        // user.label è "Nome Cognome - Settore". Prendiamo solo "Nome Cognome"
        return user ? user.label.split(" - ")[0] : "Utente sconosciuto";
      })
      .join(", ");
  };

  return (
    <div className="avvisi-container">
      <h1 className="page-title">Gestione Avvisi Dipendenti</h1>

      <div className="avvisi-editor-card">
        <h2>{editingId ? "Modifica Avviso" : "Nuovo Avviso"}</h2>
        <form onSubmit={handleSubmit} className="avvisi-form">
          <div className="form-group">
            <label>Titolo</label>
            <input
              type="text"
              required
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              placeholder="Titolo dell'avviso"
            />
          </div>

          <div className="form-group">
            <label>Priorità</label>
            <select
              value={priorita}
              onChange={(e) => setPriorita(e.target.value)}
            >
              <option value="bassa">Bassa (Info)</option>
              <option value="media">Media (Avviso)</option>
              <option value="alta">Alta (Urgente)</option>
            </select>
          </div>

          <div className="form-group">
            <label>Destinatari (lascia vuoto per tutti)</label>
            <Select
              isMulti
              options={userOptions}
              value={selectedUsers}
              onChange={setSelectedUsers}
              placeholder="Seleziona utenti specifici..."
              className="basic-multi-select"
              classNamePrefix="select"
            />
          </div>

          <div className="form-group">
            <label>Contenuto</label>
            <textarea
              required
              value={contenuto}
              onChange={(e) => setContenuto(e.target.value)}
              placeholder="Scrivi qui il messaggio per i dipendenti..."
              rows="4"
            ></textarea>
          </div>

          <div className="form-actions">
            {editingId && (
              <button type="button" className="btn-cancel" onClick={resetForm}>
                Annulla
              </button>
            )}
            <button type="submit" className="btn-submit">
              {editingId ? "Aggiorna Avviso" : "Pubblica Avviso"}
            </button>
          </div>
        </form>
      </div>

      <div className="avvisi-list-section">
        <h2>Storico Avvisi</h2>
        {error && <p className="error">{error}</p>}
        {avvisi && avvisi.length === 0 && (
          <p className="no-data">Nessun avviso presente.</p>
        )}

        <div className="avvisi-grid">
          {avvisi &&
            avvisi.map((avviso) => (
              <div
                key={avviso.id}
                className={`avviso-card priority-${avviso.priorita} ${
                  !avviso.visibile ? "disabled" : ""
                }`}
              >
                <div className="avviso-header">
                  <span className="avviso-date">
                    {avviso.createdAt &&
                      format(avviso.createdAt.toDate(), "d MMM yyyy, HH:mm", {
                        locale: it,
                      })}
                  </span>
                  <span className={`badge badge-${avviso.priorita}`}>
                    {avviso.priorita}
                  </span>
                </div>
                <h3>{avviso.titolo}</h3>
                <p className="avviso-destinatari">
                  <strong>Destinatari:</strong>{" "}
                  {getDestinatariNames(avviso.destinatari)}
                </p>
                <p className="avviso-content">{avviso.contenuto}</p>
                <div className="avviso-footer">
                  <span className="author">Autore: {avviso.autore}</span>
                  <div className="avviso-actions">
                    <button
                      className="btn-icon"
                      onClick={() => toggleVisibility(avviso)}
                      title={
                        avviso.visibile ? "Nascondi avviso" : "Mostra avviso"
                      }
                    >
                      {avviso.visibile ? "👁️" : "🚫"}
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(avviso)}
                      title="Modifica"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon delete"
                      onClick={() => handleDelete(avviso.id)}
                      title="Elimina"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
